import React, { useMemo } from "react";

import { Formik } from "formik";
import { isNil } from "lodash";
import omitBy from "lodash/omitBy";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import {
  deleteAttachment,
  ManualAdjustmentAction,
  setModalContext,
  uploadRequest,
} from "actions";

import { ZoomyDialog } from "components/MaterialDialog";
import { GstMethod, PricingMethod } from "constants/billing";
import { ModalTypes } from "constants/navigation";
import {
  getSundryBusinessIdFromDocument,
  MANUAL_TEMPLATE_ID,
  getSuggestedSundryBusinessFieldsFromBillingDocumentAndTemplate,
  SundryPartyType,
  SundryPartyTypeLookup,
} from "constants/sundryTemplates";

import { EMPTY_OBJECT, shallowObjectChanges } from "lib";

import { getLivestockSaleId } from "lib/navigation";

import {
  getActiveLivestockAgentDeployment,
  getAttachmentsByManualAdjustmentId,
  getBillingDocumentById,
  getBusinessById,
  getContextByModalType,
  getIsLivestockAgent,
  getManualAdjustments,
  selectActiveLivestockAgentDeploymentMasterBusinessId,
} from "selectors";

import { getSundryTemplateById } from "selectors/sundryTemplate";

import { LightBox } from "components/LightBox";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { DocumentTypes } from "constants/documentTypes";
import SundryForm from "./SundryForm";
import { SundryFormValues } from "./types";

const validationSchema = Yup.object().shape({
  sundryTemplateId: Yup.string().required("Required!"),
  toBusinessId: Yup.string()
    .nullable()
    .required("Required")
    .test("valid", "Required", val => val !== "-1"),
  fromBusinessId: Yup.string()
    .nullable()
    .required("Required")
    .test("valid", "Required", val => val !== "-1"),
  note: Yup.string().required("Required"),
  glCode: Yup.string().when("sundryTemplateId", {
    is: value => value !== MANUAL_TEMPLATE_ID.toString(),
    then: schema => schema.required("Required!"),
    otherwise: schema => schema.nullable(),
  }),
  taxType: Yup.string().when("sundryTemplateId", {
    is: value => value !== MANUAL_TEMPLATE_ID.toString(),
    then: schema => schema.required("Required!"),
    otherwise: schema => schema.nullable(),
  }),
  taxAmount: Yup.string().nullable().required("Required"),
  totalInc: Yup.string().nullable().required("Required"),
  subtotal: Yup.string().nullable().required("Required"),
});

interface HardCodedFromTemplate {
  fromBusinessId?: string;
  toBusinessId?: string;
}

interface ModalProps {
  id: string | null;
  fromBusinessId: string;
  toBusinessId: string;
  labels: string[];
  gstMethod: GSTMethod;
  readOnlyFields: string[];
  note: string;
  totalAmountCents: number;
  onClose: () => void;
  billingDocumentId?: string;
}

export const Modal: React.FC<ModalProps> = ({
  id,
  fromBusinessId,
  toBusinessId,
  labels,
  gstMethod,
  readOnlyFields,
  note = undefined,
  totalAmountCents = undefined,
  onClose,
  billingDocumentId = null,
}: ModalProps) => {
  const [preview, setPreview] = React.useState(null);
  const [deleteDialogFile, setDeleteFileDialog] = React.useState(null);
  const [parkedAttachmentFiles, setParkedAttachmentFiles] = React.useState([]);

  const closePreview = () => setPreview(null);

  const dispatch = useDispatch();

  const sundryAttachments: Array<File> = useSelector(
    getAttachmentsByManualAdjustmentId(id),
  );

  const isEdit = Boolean(id);

  const context =
    useSelector(getContextByModalType(ModalTypes.ManualAdjustmentForm)) ||
    EMPTY_OBJECT;

  const deleteFile = () => {
    if (isEdit) {
      dispatch(deleteAttachment(deleteDialogFile));
    } else {
      setParkedAttachmentFiles(
        parkedAttachmentFiles.filter(
          parkedAtt =>
            `${parkedAtt.name}_${parkedAtt.size}` !==
            `${deleteDialogFile.name}_${deleteDialogFile.size}`,
        ),
      );
    }
    setDeleteFileDialog(null);
  };

  const attachmentTypeOptions = [
    {
      label: "Image/Media",
      value: DocumentTypes.SUNDRY,
    },
  ];

  const billingDocument: BillingDocument = useSelector(
    getBillingDocumentById(billingDocumentId),
  );

  const documentBusinessId: string | null =
    getSundryBusinessIdFromDocument(billingDocument);

  const documentBusiness: Business = useSelector(
    getBusinessById(documentBusinessId),
  );

  const defaults: Partial<SundryFormValues> = {
    fromBusinessId,
    toBusinessId,
    labels,
    note,
    totalAmountCents,
    gstMethod,
    pricingMethod: PricingMethod.GROSS_PRICE,
  };

  const handleClose = () => {
    dispatch(setModalContext(ModalTypes.ManualAdjustmentForm, null));
    onClose();
  };

  const manualAdjustment: ManualAdjustment =
    useSelector(getManualAdjustments)?.[id] || EMPTY_OBJECT;

  const activeSundryTemplate: SundryTemplate = useSelector(
    getSundryTemplateById(
      manualAdjustment.sundryTemplateId || context.sundryTemplateId,
    ),
  );

  const deploymentMasterBusinessId: string = useSelector(
    selectActiveLivestockAgentDeploymentMasterBusinessId,
  );

  const defaultsFromTemplate = {
    fromBusinessId: activeSundryTemplate.paidToBusinessId, // Billed From
    toBusinessId: activeSundryTemplate.paidFromBusinessId, // Billed To
    glCode: activeSundryTemplate.glCode,
    gstMethod: activeSundryTemplate.gstMethod,
    taxType: activeSundryTemplate.taxType,
    note: activeSundryTemplate.note,
    labels: activeSundryTemplate.labels,
  };

  const { fromType, toType }: ValueInObj<typeof SundryPartyTypeLookup> =
    SundryPartyTypeLookup[activeSundryTemplate?.label] || {
      fromType: "",
      toType: "",
    };

  const hardCodedFromTemplate: HardCodedFromTemplate = {};
  if (fromType === SundryPartyType.DEPLOYMENT) {
    hardCodedFromTemplate.toBusinessId = deploymentMasterBusinessId;
  }
  if (toType === SundryPartyType.DEPLOYMENT) {
    hardCodedFromTemplate.fromBusinessId = deploymentMasterBusinessId;
  }

  const sundryTemplateId: string | number | undefined = useMemo(() => {
    // If we have an active template, use it.
    if (activeSundryTemplate?.id) {
      return activeSundryTemplate.id;
    }
    // If we have some context about manual labels, set to manual
    if (defaults.labels || manualAdjustment.labels) {
      return MANUAL_TEMPLATE_ID;
    }
    return null;
  }, [activeSundryTemplate.id, defaults.labels, manualAdjustment.labels]);

  // if a billing document is passed in - prefill businesses based on them
  const prefilledBusinessesFromDocument = billingDocument
    ? getSuggestedSundryBusinessFieldsFromBillingDocumentAndTemplate(
        billingDocument?.type,
        activeSundryTemplate,
        documentBusiness,
      )
    : {};

  const initialValues: SundryFormValues = {
    // Variable names danced so that we can make use shared common in usePricingFormContext.
    fromBusinessId: null,
    toBusinessId: null,
    taxAmount: manualAdjustment.gstCents,
    subtotal: manualAdjustment.subtotalCents,
    totalInc: manualAdjustment.totalCents,
    gstMethod: GstMethod.GST_INCLUSIVE,
    ...omitBy(defaultsFromTemplate, isNil),
    ...omitBy(defaults, isNil),
    ...manualAdjustment,
    sundryTemplateId,
    ...context,
    ...hardCodedFromTemplate,
    ...prefilledBusinessesFromDocument,
  };

  const deploymentId: number | null = useSelector(state =>
    getIsLivestockAgent(state)
      ? getActiveLivestockAgentDeployment(state).id
      : null,
  );

  const onSubmit: (rawValues: SundryFormValues) => void = (
    rawValues: SundryFormValues,
  ) => {
    // Variable names danced so that we can make use shared common in usePricingFormContext.
    const values = {
      fromBusinessId: rawValues.fromBusinessId,
      toBusinessId: rawValues.toBusinessId,
      note: rawValues.note,
      subtotalCents: rawValues.subtotal,
      gstMethod: rawValues.gstMethod,
      gstCents: rawValues.taxAmount,
      glCode: rawValues.glCode,
      taxType: rawValues.taxType,
      billingReference: rawValues.billingReference,
      sundryTemplateId:
        rawValues.sundryTemplateId.toString() === MANUAL_TEMPLATE_ID.toString()
          ? null
          : rawValues.sundryTemplateId,
      labels: rawValues.labels,
    };

    const { createAnother } = rawValues;

    if (id) {
      const payload = {
        id,
        ...shallowObjectChanges(values, manualAdjustment),
      };

      dispatch(ManualAdjustmentAction.update(payload));
    } else {
      const newManualAdjustmentId = uuidv4();

      const payload = {
        ...values,
        id: newManualAdjustmentId,
        livestockSaleId: getLivestockSaleId(),
        deploymentId,
      };

      dispatch(ManualAdjustmentAction.create(payload));

      if (parkedAttachmentFiles.length) {
        let formData = {};
        for (const file of parkedAttachmentFiles) {
          formData = {
            manual_adjustment: newManualAdjustmentId,
            type: DocumentTypes.SUNDRY,
          };
          dispatch(uploadRequest(file, formData));
        }
      }
    }

    if (createAnother) {
      const newContext = {
        sundryTemplateId: rawValues.sundryTemplateId,
        labels: values.labels,
        key: uuidv4(),
        createAnother: true,
      };
      dispatch(setModalContext(ModalTypes.ManualAdjustmentForm, newContext));
    } else {
      handleClose();
    }
  };

  return (
    <>
      <ZoomyDialog
        open
        onClose={handleClose}
        maxWidth={undefined}
        fullWidth={undefined}
      >
        <React.Fragment
          key={`${initialValues.sundryTemplateId}-${context.key}`}
        >
          <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
          >
            <SundryForm
              billingDocumentId={billingDocumentId}
              id={id}
              readOnlyFields={[
                ...(readOnlyFields || []),
                ...Object.keys(hardCodedFromTemplate),
              ]}
              onClose={handleClose}
              attachments={sundryAttachments}
              parkedAttachmentFiles={parkedAttachmentFiles}
              setDeleteFileDialog={setDeleteFileDialog}
              setPreview={setPreview}
              setParkedAttachmentFiles={setParkedAttachmentFiles}
            />
          </Formik>
        </React.Fragment>
      </ZoomyDialog>
      {sundryAttachments?.length > 0 && preview && (
        <LightBox
          defaultId={preview}
          onClose={closePreview}
          images={sundryAttachments || parkedAttachmentFiles}
          attachmentTypeOptions={attachmentTypeOptions}
          readOnly={false}
        />
      )}
      {deleteDialogFile && (
        <ConfirmDialog
          title={createModalTitle("this Attachment")}
          isOpen={deleteDialogFile}
          onCancel={() => setDeleteFileDialog(null)}
          onDelete={deleteFile}
        />
      )}
    </>
  );
};
